import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EngagementEvent } from '../../mockData';
import { Portal } from '../../../../components/Portal/Portal';
import './EventDetailsModal.scss';

interface EventDetailsModalProps {
  event: EngagementEvent;
  isOpen: boolean;
  onClose: () => void;
  onBook: () => void;
}

export const EventDetailsModal: React.FC<EventDetailsModalProps> = ({
  event,
  isOpen,
  onClose,
  onBook
}) => {
  const { t, i18n } = useTranslation('home');
  const { t: eventT } = useTranslation('events');

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!event.details || !isOpen) return null;

  return (
    <Portal>
      <div className="event-details-modal-overlay" onClick={onClose}>
        <div 
          className="event-details-modal"
          onClick={e => e.stopPropagation()}
        >
          <button 
            className="event-details-modal__close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
          <div className="event-details-modal__content">
        <h2 className="event-details-modal__title">{event.title}</h2>
        <div className="event-details-modal__type">{eventT(`eventType.${event.details.type === 'In-Person' ? 'inPerson' : 'virtual'}`)}</div>
        <div className="event-details-modal__info">
          <div className="event-details-modal__datetime">
            <div className="event-details-modal__date">{event.details.date}</div>
            <span className="event-details-modal__date-separator">.</span>
            <div className="event-details-modal__time">{event.details.time}</div>
          </div>
          <div className="event-details-modal__duration">
            <span className="event-details-modal__label">{t('engagement.eventDetails.duration')}:</span>
            <span className="event-details-modal__value">{event.details.duration}</span>
          </div>
        </div>
        <div className="event-details-modal__description">
          {event.details.description}
        </div>
        <div className="event-details-modal__divider" />
        {/* {event.details.location && (
          <div className="event-details-modal__location">
            <span className="event-details-modal__value">{event.details.location}</span>
            <br />
          </div>
        )} */}
        <span className="event-details-modal__value_checkEvents">
          {t('engagement.checkEvents').split('Events').map((part, index, array) => {
            // If this is the last part, just return it
            if (index === array.length - 1) {
              return <React.Fragment key={index}>{part}</React.Fragment>;
            }
            // Otherwise, return this part followed by the "Events" link
            return (
              <React.Fragment key={index}>
                {part}
                <Link to="/events" style={{ fontWeight: 'bold' }}>
                  {i18n.language === 'fr' ? 'Événements ' : 'Events '}
                </Link>
              </React.Fragment>
            );
          })}
        </span>
        <button className="event-details-modal__book" onClick={onBook}>
          {t('engagement.eventDetails.book')}
        </button>
          </div>
        </div>
      </div>
    </Portal>
  );
};
